import { useTranslation } from "react-i18next"
import { observer } from "mobx-react-lite"
import { MouseEvent, useState } from "react"
import { useStore } from "../../../contexts/store"
import { Folder } from "../../../shared/src/types/Content"
import { MaterialListType, PodClass } from "../../../classes/Pod"
import { Op_deleteFolder } from "../../../shared/src/types/Ops"

import ConfirmDialog from "../../Elements/ConfirmDialog"
import PodMaterialFile from "./PodMaterialFile"
import ReadingProgress from "../../Elements/ReadingProgress"
import SettingsDialog from "./SettingsDialog"
import UxIcon from "../../Elements/UxIcon"
import { MaterialItem, VisibilityWarning } from "./PodMaterialHelper"

import { Accordion, AccordionDetails, AccordionSummary } from "../../Elements/Accordion"
import { Box, IconButton } from "@mui/material"


const PodMaterialFolder = ({clone, isEditMode, folder, files}: {clone?: boolean, isEditMode?: boolean, folder: Folder, files: MaterialListType[]}) => {
  const { t } = useTranslation()
  const { opStore, podStore, sessionStore, uiStore } = useStore()
  const [openSettings, setOpenSettings] = useState(false)
  const [showDeleteDialog, setShowDeleteDialog] = useState(false)
  const pod: PodClass | null = podStore.pod
  if(pod === null) return null

  const allowedDeleteFolder = pod.isAllowed('deleteFolder', folder.folderId)
  const allowedEditFolder =  pod.isAllowed('editFolder', folder.folderId)
  const expanded = (uiStore.folderIsOpen[pod.podId] && uiStore.folderIsOpen[pod.podId][folder.folderId]) ? true : false


  const handleDeleteFolder = () => {
    if (sessionStore.session) {
      const op:Op_deleteFolder = {
        op: 'deleteFolder',
        podId: pod.podId,
        data: {
          folderId: folder.folderId,
          usergroupId: pod.getUsergroupByRole('Pod').usergroupId,
          userId: sessionStore.session.user.userId,
          userName: podStore.userPseudonym || '',
        }
      }
      opStore.doOp(op)
    }
  }

  const handleExpandFolder = () => {
    uiStore.setFolderIsOpen(pod.podId, folder.folderId, !(uiStore.folderIsOpen[pod.podId] && uiStore.folderIsOpen[pod.podId][folder.folderId]))
  }

  const handleOpenSettings = (e: MouseEvent) => {
    setOpenSettings(true)
    e.stopPropagation()
  }

  if(isEditMode) return (
    <>
      <MaterialItem item={folder} itemType={"folder"}>
        {clone === undefined &&
          <>
            <VisibilityWarning hidden={folder.hidden} />
            <Box sx={{display: "grid", gridTemplateColumns: "max-content max-content", gap: "5px"}}>
              {allowedDeleteFolder &&
                <IconButton sx={{padding: "10px"}} size="small" onClick={() => setShowDeleteDialog(true)}>
                  <UxIcon fontSize="small" id="delete" />
                </IconButton>
              }
              {allowedEditFolder &&
                <IconButton sx={{padding: "10px"}} size="small" onClick={(e: MouseEvent) => handleOpenSettings(e)}>
                  <UxIcon fontSize="small" id="settings" />
                </IconButton>
              }
            </Box>
          </>
        }
      </MaterialItem>
      <SettingsDialog handleClose={() => setOpenSettings(false)} itemType="folder" item={folder} open={openSettings}  pod={pod} />
      <ConfirmDialog
        handleClose={() => { setShowDeleteDialog(false)} }
        info={t('The files in the folder are not deleted.') || ''}
        onOk={handleDeleteFolder}
        open={showDeleteDialog}
        title={t('Delete the folder {{folderName}}?', {folderName: folder.name})}
      />
    </>
  )

  return (
    <>
    <Accordion expanded={expanded} onChange={handleExpandFolder}>
      <AccordionSummary component={"div"} sx={{backgroundColor: expanded ? "#f9f4f4" : "white", borderBottom: "1px solid #ededed"}}>
        <MaterialItem expanded={expanded} item={folder} itemType={"folder"}>
          <ReadingProgress folderId={folder.folderId} />
          <VisibilityWarning hidden={folder.hidden} />
          {allowedEditFolder &&
            <IconButton sx={{padding: "10px"}} size="small" onClick={(e: MouseEvent) => handleOpenSettings(e)}>
              <UxIcon fontSize="small" id="settings" />
            </IconButton>
          }
        </MaterialItem>
      </AccordionSummary>
      <AccordionDetails sx={{
        backgroundColor: "#f7f7f78a",
        borderBottom: "1px solid #f9f4f4",
      }}>
        {files.map(item => {
          if(item.file) return <PodMaterialFile key={item.id} file={item.file} />
            return null
          })
        }
      </AccordionDetails>
    </Accordion>
    <SettingsDialog handleClose={() => setOpenSettings(false)} itemType="folder" item={folder} open={openSettings}  pod={pod} />
    </>
  )
}

export default observer(PodMaterialFolder)
